import { calcBaseConfig } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/baseConfig'
import { sortData } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/sortData'
import { removeLogicFromText, transformText, noTextPlaceholder } from '@aytm/app/stats/page/components/lib/utils'
import { wrapTooltip } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/tooltipFormatter'
import { CHART_HEIGHT } from '@aytm/app/stats/page/question-views/components/lib/constants'

const CHART_MARGIN = [84, 37, 50, 130]

const calcAreaIngot = () => {
  return calcBaseConfig({
    chart: {
      animation: false,
      backgroundColor: 'transparent',
      inverted: true,
      margin: [...CHART_MARGIN],
      type: 'area'
    },
    plotOptions: {
      area: {
        shadow: false,
        stacking: 'percent',
        lineColor: '#ffffff',
        lineWidth: 1,
        fillOpacity: 0.9,
        marker: {
          lineWidth: 1,
          lineColor: '#ffffff'
        }
      },
      series: {
        animation: false,
        borderWidth: 1,
        events: {
          legendItemClick: function (e) {
            if (this.chart.pointState) {
              e.preventDefault()
            }
          }
        },
        marker: {
          enabled: false
        },
        shadow: false,
        states: {
          hover: {
            brightness: 0.05
          },
          inactive: {
            opacity: 1
          }
        }
      }
    },
    legend: {
      itemHiddenStyle: {
        color: '#cccccc',
        fontWeight: '300',
        textDecoration: 'none'
      },
      itemStyle: {
        color: '#333333',
        fontWeight: '400'
      }
    },
    xAxis: {
      categories: [],
      labels: {
        autoRotation: 0,
        autoRotationLimit: 0,
        enabled: true
      },
      tickmarkPlacement: 'on',
      title: {
        enabled: false
      }
    },
    yAxis: {
      labels: {
        autoRotation: 0,
        autoRotationLimit: 0
      },
      min: 0,
      title: {
        color: '#4896BC',
        text: 'percents',
        style: {
          font: '11px Ubuntu'
        }
      }
    }
  })
}

const createAreaTooltipFormatter = percentDecimals => {
  return function () {
    const s = this.series
    const p = this.point
    const categoryText = s.options.categories?.[p.index] || ''
    const percentage = p.percentage?.toFixed(percentDecimals ?? 1) || (0).toFixed(percentDecimals ?? 1)

    return wrapTooltip(`
      <div class='highcharts-tooltip-text'>${categoryText}</div>
      <div class='highcharts-tooltip-text'>${s.name}: <b>${percentage}%</b></div>
    `)
  }
}

export const calcAreaOptions = props => {
  const data = calcAreaIngot()

  const {
    answer = {},
    batches: batchesProp = [],
    chartMode,
    labelsMode = 'entities',
    colorTheme = [],
    chartLegendMode,
    sortOrder,
    width = 658,
    height = CHART_HEIGHT
  } = props

  let batches = sortData(answer, batchesProp.slice(), sortOrder, {
    answerType: 'score',
    answerPath: 'b'
  })

  const percentsMode = Boolean(chartMode?.endsWith('percents'))

  const series = []
  let respondentsTotal = 0

  const xAxisCategories = []
  const seriesCategories = []

  const orderedColumns = []

  if (answer && batches.length && batches[0]?.answerVariants) {
    const _textSize = 12

    if (labelsMode === 'entities') {
      for (const av of batches[0].answerVariants) {
        if (!av) continue
        xAxisCategories.push(transformText(av.text, _textSize))
        seriesCategories.push(removeLogicFromText(av.text) || noTextPlaceholder(av))
      }
    } else {
      for (const b of batches) {
        if (!b) continue
        xAxisCategories.push(chartLegendMode === 'short' ? `A${b.position}` : transformText(b.text, _textSize))
        seriesCategories.push(removeLogicFromText(b.text) || noTextPlaceholder(b))
      }
    }

    let index = 0

    if (labelsMode === 'entities') {
      for (const b of batches) {
        if (!b) continue

        const bKey = `b${b.position}`
        const answerData = answer[bKey]
        if (!answerData) continue

        const obj = {
          index,
          k: bKey,
          score: answerData.score || 0,
          values: []
        }

        for (const av of b.answerVariants) {
          if (!av) continue
          const aKey = `a${av.position}`
          const avData = answerData[aKey]
          if (avData) {
            obj.values.push(percentsMode ? avData.percents || 0 : avData.amount || 0)
          } else {
            obj.values.push(0)
          }
        }

        orderedColumns.push(obj)

        if (index === 0) {
          respondentsTotal = obj.values.reduce((sum, v) => sum + v, 0)
        }
        index += 1
      }

      orderedColumns.sort((a, b) => a.score - b.score)
    } else {
      for (const av of batches[0].answerVariants) {
        if (!av) continue

        const obj = {
          index,
          k: `av${av.position}`,
          score: 0,
          values: []
        }

        for (const b of batches) {
          if (!b) continue
          const answerData = answer[`b${b.position}`]
          if (!answerData) continue

          const aKey = `a${av.position}`
          const avData = answerData[aKey]
          if (avData) {
            obj.values.push(percentsMode ? avData.percents || 0 : avData.amount || 0)
          } else {
            obj.values.push(0)
          }
        }

        orderedColumns.push(obj)

        if (index === 0) {
          respondentsTotal = obj.values.reduce((sum, v) => sum + v, 0)
        }
        index += 1
      }

      orderedColumns.reverse()
    }

    const columnsLength = orderedColumns.length

    for (let eli = 0; eli < columnsLength; eli++) {
      const el = orderedColumns[eli]
      const item = labelsMode === 'entities' ? batches[el.index] : batches[0].answerVariants[el.index]

      if (!item) continue

      const colorIndex = columnsLength - eli - 1
      const itemText = removeLogicFromText(item.text) || noTextPlaceholder(item)

      series.push({
        // only a ranked item (a batch) takes the short A# label; a rank keeps its text
        name: labelsMode === 'entities' && chartLegendMode === 'short' ? `A${item.position}` : itemText,
        color: colorTheme[colorIndex % colorTheme.length],
        colorIndex,
        data: el.values,
        categories: seriesCategories
      })
    }
  }

  data.series = series
  data.xAxis.categories = xAxisCategories
  data.yAxis.title.text = percentsMode ? 'percents' : 'respondents'

  data.tooltip = {
    ...data.tooltip,
    formatter: createAreaTooltipFormatter(props.reportPercentDecimals)
  }

  if (!percentsMode) {
    data.plotOptions.area.stacking = 'normal'
    data.yAxis.min = 0
    data.yAxis.max = respondentsTotal
    data.yAxis.minTickInterval = 1
  }

  if (data.chart) {
    data.chart.width = width
    data.chart.height = height
  }

  return { options: data }
}
